import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CoreService } from 'src/app/services/core.service';
import { LoaderService } from 'src/app/services/loader.service';
import { UsersService } from 'src/app/services/users.service';
import { DialogNewAddressComponent } from '../dialog-new-address/dialog-new-address.component';

export interface AddressMapReference {
  latitude: number;
  longitude: number;
  address?: string;
}

interface DialogAddressData {
  referenceLocation?: AddressMapReference;
}

@Component({
  selector: 'app-dialog-address',
  templateUrl: './dialog-address.component.html',
  styleUrls: ['./dialog-address.component.scss']
})
export class DialogAddressComponent implements OnInit {
  addresses: any[] = [];
  selectedAddress: any = null;
  isLoading = false;

  constructor(
    private loaderService: LoaderService,
    private coreService: CoreService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<DialogAddressComponent>,
    private usersService: UsersService,
    @Inject(MAT_DIALOG_DATA) private data: DialogAddressData | null
  ) {
    // console.log(this.data)
  }

  ngOnInit(): void {
    this.getAddresses();
  }

  getAddresses(addressToSelect: any = this.selectedAddress): void {
    const selectedAddressId = addressToSelect?.id;

    this.isLoading = true;
    this.loaderService.show();
    this.usersService.getAddress().subscribe({
      next: (response) => {
        if (response.status) {
          this.addresses = response.data ?? [];
          this.selectedAddress = this.findSelectedAddress(addressToSelect, selectedAddressId);
        } else {
          this.showError();
        }

        this.finishLoading();
      },
      error: (error) => {
        console.error(error);
        this.showError();
        this.finishLoading();
      }
    });
  }

  selectAddress(address: any): void {
    this.selectedAddress = address;
  }

  confirmSelection(): void {
    if (this.selectedAddress) {
      // Mantiene el contrato del selector anterior para el flujo de envío.
      this.dialogRef.close({ address: this.selectedAddress });
    }
  }

  openNewAddress(): void {
    const isMobile = window.innerWidth <= 768;

    const dialogRef = this.dialog.open(DialogNewAddressComponent, {
      width: isMobile ? '94vw' : '780px',
      maxWidth: '95vw',
      height: 'auto',
      maxHeight: 'calc(100vh - 30px)',
      autoFocus: false,
      backdropClass: 'nina-dialog-backdrop',
      data: {
        referenceLocation: this.data?.referenceLocation
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.created) {
        this.getAddresses(result.address ?? result.addressData);
      }
    });
  }

  onAddressKeydown(event: KeyboardEvent, address: any): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectAddress(address);
    }
  }

  isSelected(address: any): boolean {
    return this.selectedAddress?.id === address?.id;
  }

  getLocalityLabel(address: any): string {
    const zoneType = address?.locality?.zone_type;
    return zoneType ? this.capitalize(zoneType) : 'Colonia';
  }

  formatPhone(phone: string | number | null | undefined): string {
    const value = String(phone ?? '');
    const normalized = value.length === 12 && value.startsWith('52')
      ? value.substring(2)
      : value;

    return normalized || 'No registrado';
  }

  close(): void {
    this.dialogRef.close();
  }

  private finishLoading(): void {
    this.isLoading = false;
    this.loaderService.hide();
  }

  private showError(): void {
    this.coreService.emitSubjet.next({
      type: 'Snackbar',
      payload: { text: 'Hubo un error al cargar tus direcciones.' }
    });
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  private findSelectedAddress(addressToSelect: any, selectedAddressId: any): any {
    if (!addressToSelect) {
      return null;
    }

    return this.addresses.find((address) => address.id === selectedAddressId)
      ?? this.addresses.find((address) =>
        String(address.lat) === String(addressToSelect.lat)
        && String(address.lng) === String(addressToSelect.lng)
        && address.street === addressToSelect.street
        && address.ext_number === addressToSelect.ext_number
      )
      ?? null;
  }

}
